import {
  type ChildMachineConfig,
  ParentState,
  PaymentInformationChildState,
  PaymentInformationState,
} from "@checkout/index";
import { merge } from "lodash-es";
import { ActionTypes } from "./actions";
import { CardPaymentEventTypes } from "./events";
import { GuardTypes } from "./guards";
import { ServiceTypes } from "./services";

export type CardPaymentChildMachineConfig = ChildMachineConfig & {
  addNextState?: string;
};

export const getMachineState = (config: CardPaymentChildMachineConfig) => ({
  id: ParentState.cardPayment,
  initial: PaymentInformationState.loading,
  always: {
    actions: ActionTypes.setPaymentVisited,
    cond: GuardTypes.notVisitedPayment,
  },
  states: merge(
    {
      [PaymentInformationState.loading]: {
        invoke: {
          src: ServiceTypes.listPaymentMethods,
          onDone: {
            target: `#${config.parentMachineId}.${ParentState.cardPayment}.${PaymentInformationState.idle}`,
            actions: ActionTypes.handleListPayments,
          },
          onError: {
            target: `#${config.parentMachineId}.${ParentState.cardPayment}.${PaymentInformationState.error}`,
            actions: ActionTypes.setPaymentError,
          },
        },
      },
      [PaymentInformationState.idle]: {
        on: {
          [CardPaymentEventTypes.SET_SELECTED_PAYMENT_METHOD_ID]: {
            actions: ActionTypes.setSelectedPaymentMethodId,
          },
        },
      },
      [PaymentInformationState.add]: {
        initial: PaymentInformationChildState.idle,
        states: {
          [PaymentInformationChildState.idle]: {},
          [PaymentInformationChildState.verify]: {
            invoke: {
              src: ServiceTypes.verifyPayment,
              onDone: {
                actions: ActionTypes.setPaymentMethodId,
                target: config.addNextState ?? config.nextState,
              },
              onError: {
                actions: ActionTypes.setPaymentError,
                target: `#${config.parentMachineId}.${ParentState.cardPayment}.${PaymentInformationState.error}`,
              },
            },
          },
        },
      },
      [PaymentInformationState.delete]: {
        invoke: {
          src: ServiceTypes.deletePaymentMethod,
          onDone: {
            actions: ActionTypes.deletePaymentMethod,
            target: `#${config.parentMachineId}.${ParentState.cardPayment}.${PaymentInformationState.idle}`,
          },
          onError: {
            actions: ActionTypes.setPaymentError,
            target: `#${config.parentMachineId}.${ParentState.cardPayment}.${PaymentInformationState.error}`,
          },
        },
      },
      [PaymentInformationState.error]: {
        on: {
          [CardPaymentEventTypes.CLEAR_PAYMENT_ERROR]: {
            target: `#${config.parentMachineId}.${ParentState.cardPayment}.${PaymentInformationState.idle}`,
          },
        },
      },
    },
    config.stateOverride
  ),
  on: {
    [CardPaymentEventTypes.PREVIOUS]: {
      target: config.previousState,
    },
    [CardPaymentEventTypes.NEXT]: {
      target: config.nextState,
    },
    [CardPaymentEventTypes.OPEN_PAYMENT_FORM]: `#${config.parentMachineId}.${ParentState.cardPayment}.${PaymentInformationState.add}`,
    [CardPaymentEventTypes.CLOSE_PAYMENT_FORM]: `#${config.parentMachineId}.${ParentState.cardPayment}.${PaymentInformationState.idle}`,
    [CardPaymentEventTypes.VERIFY_PAYMENT_METHOD]: {
      actions: ActionTypes.setSpreedlyToken,
      target: `#${config.parentMachineId}.${ParentState.cardPayment}.${PaymentInformationState.add}.${PaymentInformationChildState.verify}`,
    },
    [CardPaymentEventTypes.DELETE_PAYMENT_METHOD]: {
      target: `#${config.parentMachineId}.${ParentState.cardPayment}.${PaymentInformationState.delete}`,
    },
    [CardPaymentEventTypes.SET_PAYMENT_ERROR]: {
      actions: ActionTypes.setPaymentError,
      target: `#${config.parentMachineId}.${ParentState.cardPayment}.${PaymentInformationState.error}`,
    },
  },
});
